import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Trash2, AlertCircle } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useAuthStore } from '../store/authStore'
import { useGameStore } from '../store/gameStore'
import PlantPreview from '../components/PlantPreview'
import Button from '../components/Button'
import Modal from '../components/Modal'
import { useI18n } from '../i18n/I18nProvider'

function MyPublishedPlants() {
  const [plants, setPlants] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedPlant, setSelectedPlant] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showSacrificeModal, setShowSacrificeModal] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  
  const user = useAuthStore((state) => state.user)
  const addSeeds = useGameStore((state) => state.addSeeds)
  const navigate = useNavigate()
  const { t } = useI18n()

  useEffect(() => {
    if (!user) {
      navigate('/auth')
      return
    }
    loadMyPlants()
  }, [user, navigate])

  const loadMyPlants = async () => {
    try {
      setLoading(true)
      const { data: { user: authUser } } = await supabase.auth.getUser()
      
      if (!authUser) return

      const { data, error } = await supabase
        .from('community_plants')
        .select('*')
        .eq('user_id', authUser.id)
        .order('created_at', { ascending: false })

      if (error) throw error
      setPlants(data || [])
    } catch (error) {
      console.error('Error loading plants:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!selectedPlant) return
    
    setActionLoading(true)
    try {
      const data = await supabase
        .from('community_plants')
        .delete()
        .eq('id', selectedPlant.id)

      if (data.error) throw data.error

      setPlants(plants.filter(p => p.id !== selectedPlant.id))
      setShowDeleteModal(false)
      setSelectedPlant(null)
    } catch (error) {
      console.error('Error deleting plant:', error)
      alert(t('published.deleteError'))
    } finally {
      setActionLoading(false)
    }
  }

  const isPlantMature = (plant) => {
    if (!plant || !plant.config) return false
    const totalClicks = (plant.config.clicksPerStage || 0) * (plant.config.stages || 0)
    if (totalClicks === 0) return false
    return (plant.progress || 0) >= totalClicks
  }

  const getMaturePlantCount = (communityPlant) => {
    const data = communityPlant?.plant_data
    if (!data) return 0
    if (Array.isArray(data.plants)) {
      return data.plants.filter(isPlantMature).length
    }
    return isPlantMature(data) ? 1 : 0
  }

  const getGardenSacrificeReward = (communityPlant) => {
    const matureCount = getMaturePlantCount(communityPlant)
    const likes = communityPlant?.likes || 0
    return {
      matureCount,
      likes,
      total: matureCount + likes,
    }
  }

  const handleOpenSacrificeModal = (plant) => {
    setSelectedPlant(plant)
    setShowSacrificeModal(true)
  }

  const handleConfirmGardenSacrifice = async () => {
    if (!selectedPlant) return

    const { total } = getGardenSacrificeReward(selectedPlant)

    setActionLoading(true)
    try {
      const data = await supabase
        .from('community_plants')
        .delete()
        .eq('id', selectedPlant.id)

      if (data.error) throw data.error

      if (total > 0) {
        addSeeds(total)
      }

      setPlants(plants.filter((p) => p.id !== selectedPlant.id))
      setShowSacrificeModal(false)
      setSelectedPlant(null)
    } catch (error) {
      console.error('Error sacrificing garden:', error)
      alert(t('published.sacrificeError'))
    } finally {
      setActionLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-green-600"></div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl font-bold text-green-800 mb-4 text-center">
          {t('published.title')}
        </h1>
        <p className="text-gray-600 text-center mb-12">
          {t('published.subtitle')}
        </p>

        {plants.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl shadow-lg">
            <AlertCircle size={64} className="mx-auto mb-4 text-gray-400" />
            <p className="text-gray-600 text-lg mb-4">
              {t('published.emptyTitle')}
            </p>
            <Button onClick={() => navigate('/garden')}>
              {t('published.emptyCta')}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {plants.map((plant) => (
              <div
                key={plant.id}
                className="bg-white rounded-xl shadow-lg overflow-hidden"
              >
                {/* Plant Preview */}
                <div className="bg-gradient-to-b from-sky-300 to-green-100 aspect-square flex items-center justify-center overflow-hidden">
                  {plant.plant_data?.snapshot ? (
                    <img
                      src={plant.plant_data.snapshot}
                      alt={t('published.snapshotAlt', 'Shared garden preview')}
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  ) : (
                    <PlantPreview plantData={plant} size={300} />
                  )}
                </div>

                {/* Plant Info */}
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-gray-500">
                      {t('published.stageLabel')} {plant.stage || 0}
                    </span>
                    <span className="text-sm font-bold text-green-600">
                      {t('published.likeLabel')} {plant.likes || 0} likes
                    </span>
                  </div>

                  <p className="text-xs text-gray-400 mb-4">
                    {t('published.publishedLabel')} {new Date(plant.created_at).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </p>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button
                      variant="danger"
                      onClick={() => handleOpenSacrificeModal(plant)}
                    >
                      {t('published.sacrificeButton')}
                    </Button>
                    <Button
                      onClick={() => {
                        setSelectedPlant(plant)
                        setShowDeleteModal(true)
                      }}
                      variant="secondary"
                      className="bg-red-500 hover:bg-red-600 text-white rounded-full aspect-square flex items-center justify-center"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Delete Modal */}
        <Modal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title={t('published.deleteModalTitle')}
          size="sm"
        >
          <p className="mb-6">
            {t('published.deleteModalBody')}
          </p>
          <div className="flex gap-4">
            <Button
              onClick={handleDelete}
              className="flex-1 bg-red-500 hover:bg-red-600"
              disabled={actionLoading}
            >
              {actionLoading ? t('published.deleting') : t('published.deleteConfirm')}
            </Button>
            <Button
              onClick={() => setShowDeleteModal(false)}
              variant="secondary"
              className="flex-1"
              disabled={actionLoading}
            >
              {t('published.cancel')}
            </Button>
          </div>
        </Modal>

        {/* Garden Sacrifice Modal */}
        <Modal
          isOpen={showSacrificeModal}
          onClose={() => setShowSacrificeModal(false)}
          title={t('published.sacrificeModalTitle')}
          size="sm"
        >
          {selectedPlant ? (
            <>
              {(() => {
                const { total, matureCount, likes } = getGardenSacrificeReward(selectedPlant)
                const seedLabel = total > 1 ? t('published.seedPlural') : t('published.seedSingular')
                const matureLabel = matureCount > 1 ? t('published.maturePlural') : t('published.matureSingular')
                const likeLabel = likes > 1 ? t('published.likePlural') : t('published.likeSingular')
                return (
                  <>
                    <p className="mb-2">
                      {t('published.sacrificeReward')} {total} {seedLabel}.
                    </p>
                    <p className="text-sm text-gray-600 mb-6">
                      {t('published.sacrificeCalc')} {matureCount} {matureLabel} + {likes} {likeLabel}.
                    </p>
                  </>
                )
              })()}
            </>
          ) : (
            <p className="mb-6">{t('published.selectGardenToSacrifice')}</p>
          )}

          <div className="flex gap-4">
            <Button
              onClick={handleConfirmGardenSacrifice}
              className="flex-1 bg-yellow-500 hover:bg-yellow-600"
              disabled={actionLoading || !selectedPlant}
            >
              {actionLoading ? t('published.sacrificing') : t('published.confirmSacrifice')}
            </Button>
            <Button
              onClick={() => setShowSacrificeModal(false)}
              variant="secondary"
              className="flex-1"
              disabled={actionLoading}
            >
              {t('published.cancel')}
            </Button>
          </div>
        </Modal>
      </div>
    </div>
  )
}

export default MyPublishedPlants
